import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { PicklistDto, CreatePicklistItemRequest, PostPicklistItemRequest, PostingBatchStatusDto, AdjustPicklistItemRequest, ReplaceSerialRequest } from '../types';
import { PostingStatusEnum } from '../types';
import { picklistService } from '../services';
import { useActivityRefreshStore } from '@features/activity';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { createRefreshCoordinator, type RefreshCoordinator } from './refreshCoordinator';
import {
  fetchOutboxStatus,
  fetchActiveOutboxStatuses,
  outboxKey,
  outboxType,
  groupOutboxByDocId,
  type OutboxPostingStatusDto,
} from '@shared/posting';

// 86d3mnuxe — the MIS posting lifecycle moved off the PostingBatch entity onto the OUTBOX. The picklist
// detail's active/failed "posting cards" are now projected from /api/outbox/status (getById returns
// activePostingBatches: [] post-split). Map a projected job → the PostingBatchStatusDto the detail page
// already renders, so the UI + Retry (keyed on the batch id) stay unchanged.
//   PW-02 — the batch id now arrives on the wire. It used to be regexed out of correlationId here against
//   a shape defined in C# (OutboxKeys.PicklistBatch), which meant one contract with two spellings, one of
//   them in a language that could not be checked against the other. `?? 0` keeps the old fallback for a
//   correlation that does not parse.
function mapOutboxJobToBatch(job: OutboxPostingStatusDto): PostingBatchStatusDto {
  return {
    id: job.postingBatchId ?? 0,
    status: job.status === 'Failed' ? PostingStatusEnum.PostingFailed : PostingStatusEnum.Posting,
    misTranId: null,
    postingFailedReason: job.lastError,
    postingClaimedAt: job.createdAt,
    // 86d3mnuxe funnel — reach disclosure: the failed batch card flips Retry → Reconcile (and hides
    // Cancel) when the failed attempt may have created the Inventory Adjustment.
    postReach: job.postReach,
    netSuiteRecordId: job.netSuiteRecordId,
  };
}

// Batch ids with a settle-poll currently running — dedupes a re-entrant poll (e.g. hydrate-on-open while
// the post's original poll is still looping). Both would be harmless (idempotent), but this halves the churn.
const activePolls = new Set<number>();

// PW-22 — the settle-poll's cadence. It used to be a flat 3 s with a five-minute wall-clock cap, and the cap
// was the bug: a batch still posting at 5:01 kept posting while the screen quietly stopped asking. The loop
// now runs on a CONDITION — while this batch is open — and pays for the longer life with a stepped cadence.
//
// Fast for the first minute because that is where settles overwhelmingly land: the entire transient-retry
// budget is about 15-20 seconds. After that the batch is waiting on something slow, and asking every three
// seconds tells nobody anything new.
const POLL_FAST_MS = 3_000;
const POLL_MEDIUM_MS = 10_000;
const POLL_SLOW_MS = 30_000;
const FAST_WINDOW_MS = 60_000;
const MEDIUM_WINDOW_MS = 5 * 60_000;

/** Exported for its own test: the cadence is the trade that pays for the removed ceiling, so the claim that
 *  a longer-lived poll costs FEWER requests than the old capped one has to be checkable. */
export function pollDelayMs(elapsedMs: number): number {
  if (elapsedMs < FAST_WINDOW_MS) return POLL_FAST_MS;
  if (elapsedMs < MEDIUM_WINDOW_MS) return POLL_MEDIUM_MS;
  return POLL_SLOW_MS;
}

/**
 * Resolves immediately when the tab is visible, otherwise when it next becomes visible.
 *
 * A backgrounded tab polling a full picklist graph indefinitely is not acceptable; neither is a foregrounded
 * one showing stale data on return. Awaiting this before each tick gives both: nothing is fetched while
 * hidden, and the tick that was due fires the moment the tab comes back.
 */
function whenVisible(): Promise<void> {
  if (typeof document === 'undefined' || document.visibilityState === 'visible') return Promise.resolve();
  return new Promise((resolve) => {
    const onChange = () => {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', onChange);
      resolve();
    };
    document.addEventListener('visibilitychange', onChange);
  });
}

// ── The one refresh path (owner 2026-10-08) ─────────────────────────────────────────────────────────────
// Every reload of the open picklist asks this coordinator: opening the page, every action, the page's 30-second tick
// and tab return (usePicklistAutoRefresh), "Refresh now" and the posting settle-polls. It runs one read at a time,
// gives a request made during a read ONE follow-up, never lets the tick add a read, and drops a read that a local
// write overtook. The rules and their tests live in refreshCoordinator.ts.

interface PicklistRead {
  picklist: PicklistDto;
  /** The outbox's in-flight and failed jobs; null when that read failed, so the last-known cards are kept. */
  batches: PostingBatchStatusDto[] | null;
}

async function readPicklist(id: number): Promise<PicklistRead> {
  // 86d3peur0 #11 — the OUTBOX IS READ FIRST, ON PURPOSE (mirrors receivingStore). A settled outbox (job Done)
  // happens-after the pipeline committed the MIS id, so the picklist read afterwards is guaranteed to carry it.
  // Reading the picklist first could snapshot it just BEFORE the stamp and the outbox just AFTER the job settled —
  // dropping the posting card while showing a picklist with no MIS reference. Never collapse these into Promise.all.
  let batches: PostingBatchStatusDto[] | null;
  try {
    batches = (await fetchOutboxStatus(outboxKey.picklist(id))).map(mapOutboxJobToBatch);
  } catch {
    batches = null;
  }
  return { picklist: await picklistService.getById(id, true), batches };
}

function applyPicklist(id: number, { picklist, batches }: PicklistRead) {
  const state = usePicklistStore.getState();
  usePicklistStore.setState({
    selectedPicklist: { ...picklist, activePostingBatches: batches ?? state.selectedPicklist?.activePostingBatches ?? [] },
    // PW-22 — a failed outbox read is VISIBLE while a batch is open ("stalled"); the next read clears it.
    postingTransport: {
      state: activePolls.size === 0 ? state.postingTransport.state : batches === null ? 'stalled' : 'updating',
      lastCheckedAt: new Date().toISOString(),
    },
  });
  // Every open batch gets its settle-poll, whoever started it — this tab, another tab, another person (86d3mnuxe
  // BUG 3: one poll per Posting job, deduped by batch id).
  (batches ?? [])
    .filter((b) => b.status === PostingStatusEnum.Posting)
    .forEach((b) => usePicklistStore.getState().pollPostingBatch(id, b.id, false));
}

// Typed explicitly: the store's methods return what this returns, and inferring it through the store would loop.
const picklistRefresh: RefreshCoordinator = createRefreshCoordinator<PicklistRead>({
  now: () => Date.now(),
  read: readPicklist,
  isOpen: (id) => usePicklistStore.getState().selectedPicklist?.id === id,
  snapshot: (id) => {
    const current = usePicklistStore.getState().selectedPicklist;
    return current?.id === id ? current : null;
  },
  apply: applyPicklist,
});

// The open picklist's FIRST load: one at a time per picklist. A second call while it is out — React StrictMode's double
// effect in development, a Retry pressed twice — waits for it instead of reading again; a newer first load (another
// picklist opened meanwhile) supersedes it, and the superseded one paints nothing. Once the picklist is on screen,
// every later read is the coordinator's.
let firstLoad: { id: number; token: object; done: Promise<void> } | null = null;

// "Refresh now" is a permanently visible control, so it has to tolerate a held-down click without fanning
// out picklist-graph fetches.
const REFRESH_DEBOUNCE_MS = 1_000;
let lastManualRefreshAt = 0;

interface ClosedPagedParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDesc?: boolean;
  /** PW-23 — server-side filter; the Closed tab is server-paged, so the client cannot filter it itself. */
  search?: string;
}

interface PicklistState {
  // Open picklists (Draft + Open)
  openPicklists: PicklistDto[];
  isLoadingOpen: boolean;
  openError: string | null;

  // Closed picklists (Closed + Cancelled) — server-side paged
  closedPicklists: PicklistDto[];
  closedTotalCount: number;
  closedTotalPages: number;
  /**
   * PW-12 — the tab count pills. `null` means "not known yet" and renders as a skeleton; a placeholder 0
   * would read as "there are none", which is the wrong thing to tell someone hunting an old picklist.
   */
  counts: { open: number; closed: number } | null;
  fetchCounts: () => Promise<void>;
  isLoadingClosed: boolean;
  closedError: string | null;

  // Detail page state
  selectedPicklist: PicklistDto | null;
  isLoadingDetail: boolean;
  detailError: string | null;

  // Posting state
  isPosting: boolean;

  /**
   * "Show only my picklists" — a LIST filter, persisted to localStorage.
   *
   * ⚠️ It scopes list queries and NOTHING ELSE. It used to be a `viewAll` flag passed through *every*
   * read call including `getById`, which meant turning the filter on 404'd every picklist the user had
   * not created — a table filter deciding whether a record could be opened. Record access is the
   * server's call alone: `CanViewAllPicklists` grants view-all only to `issuance.view_all_picklists`,
   * and without it the creator-or-picker rule still applies. Detail reads therefore always request
   * view-all and let the server decide.
   *
   * It also used to be mirrored in the URL and reconciled by an effect, which is what made the toggle
   * unreliable — two owners, and whichever settled last won. One owner now.
   */
  mineOnly: boolean;
  setMineOnly: (mineOnly: boolean) => void;

  // Actions
  fetchOpenPicklists: () => Promise<void>;
  fetchClosedPicklists: (params: ClosedPagedParams) => Promise<void>;
  fetchPicklistById: (id: number) => Promise<void>;
  clearSelectedPicklist: () => void;
  /** `clientKey` (E7 item 3): the open post dialog's idempotency GUID. Same key on every re-submit within
   *  one dialog, so a lost response cannot become a second partial batch. */
  postPicklist: (items: PostPicklistItemRequest[], reasonForAdjustmentId: number, requestedByEmployeeId: string, requestedByName: string | undefined, itemMemos?: Record<number, string>, itemReadings?: Record<number, number | null>, clientKey?: string) => Promise<number | { error: string }>;
  /** Retry a failed MIS post attempt (86d3kwt7h). Returns the batch id or an error. */
  retryPosting: (postingBatchId: number) => Promise<number | { error: string }>;
  /** Fire-and-forget poll of one posting batch until it settles (optimistic Posting card + detail refresh). */
  pollPostingBatch: (picklistId: number, postingBatchId: number, addOptimistic: boolean) => void;
  /** 86d3mnuxe — non-blocking: fetch the outbox posting projection for a picklist, merge its active/failed
   *  jobs into selectedPicklist.activePostingBatches, and (re)start the settle-poll if a post is in flight.
   *  Covers a post started elsewhere / a page refresh mid-post. */
  hydratePostingCards: (picklistId: number) => Promise<void>;

  /**
   * PW-22 — how batch state is arriving right now, so the strip can say something true about it.
   *
   * `updating` — a poll is running and the last check succeeded.
   * `stalled`  — a poll is running but the last check failed; it keeps trying.
   * `stopped`  — no batch on this picklist is open, so nothing is being polled.
   *
   * Read it through `usePostingTransport()`, never directly: that hook is the whole surface, and it is what
   * lets the transport change without every consumer learning it was ever a poll.
   */
  postingTransport: { state: 'updating' | 'stalled' | 'stopped'; lastCheckedAt: string | null };
  /** PW-22 — "Refresh now". Debounced, so a held-down click cannot fan out picklist-graph fetches. */
  refreshPostingCards: (picklistId: number) => Promise<void>;

  /**
   * PT-6 *H* / PT-7 A13 — re-read the selected picklist QUIETLY: no page skeleton, no page error. Reads
   * `GET {id}` with `viewAll=true` and, while that picklist is still selected, puts the result on screen with
   * the current `activePostingBatches` kept (the by-id read carries none; the settle-poll merges them the same
   * way). Sets neither `isLoadingDetail` nor `detailError` — `fetchPicklistById` sets the page-wide error on a
   * failure and the page swaps all its content for it. Returns `null`, or the error message.
   */
  reloadSelectedPicklist: (id: number) => Promise<string | null>;
  /** The page's 30-second tick and tab return: a read only if none is running or queued and the last one completed
   *  `maxAgeMs` ago or more. True when it started one, so the Activity panel can refresh on the same beat. */
  refreshPicklistIfStale: (id: number, maxAgeMs: number) => boolean;

  // Editing state
  isEditing: boolean;
  addItems: (items: CreatePicklistItemRequest[]) => Promise<boolean>;
  removeItems: (picklistItemIds: number[]) => Promise<boolean>;

  // Adjusting state (86d31p32m — bin-level, replaces void)
  isAdjusting: boolean;
  /** PT-6 — `picklistItemId` is the group anchor. Re-throws, so the dialog can branch on the status. */
  adjustItem: (picklistItemId: number, request: AdjustPicklistItemRequest) => Promise<boolean>;
  isReplacing: boolean;
  /** PT-6 E3 — the bin card's Swap serial. Re-throws, so the dialog can branch on the status. */
  replaceSerial: (picklistItemId: number, request: ReplaceSerialRequest) => Promise<boolean>;

  // Closing state
  isClosing: boolean;
  /** `true`, or the reason it was refused (the server's own words when it gave any). */
  closePicklist: (reason?: string) => Promise<true | string>;

  // Cancelling state
  isCancelling: boolean;
  /** `true`, or the reason it was refused (the server's own words when it gave any). */
  cancelPicklist: (reason: string) => Promise<true | string>;

  // Picker management state
  isManagingPickers: boolean;
  addPicker: (userId: number) => Promise<boolean>;
  removePicker: (userId: number) => Promise<boolean>;
}

export const usePicklistStore = create<PicklistState>()(persist((set, get) => ({
  // Open picklists state
  openPicklists: [],
  isLoadingOpen: false,
  openError: null,

  // PW-22 — nothing is being polled until a batch is open.
  postingTransport: { state: 'stopped', lastCheckedAt: null },

  // Closed picklists state (server-side paged)
  closedPicklists: [],
  closedTotalCount: 0,
  closedTotalPages: 0,
  counts: null,
  isLoadingClosed: false,
  closedError: null,

  // Detail page state
  selectedPicklist: null,
  isLoadingDetail: false,
  detailError: null,

  // Posting state
  isPosting: false,

  // Editing state
  isEditing: false,

  // Adjusting state (86d31p32m)
  isAdjusting: false,
  isReplacing: false,

  // Closing state
  isClosing: false,

  // Cancelling state
  isCancelling: false,

  // Picker management state
  isManagingPickers: false,

  // Defaults to showing everything the user is authorised to see; rehydrated from localStorage.
  mineOnly: false,
  setMineOnly: (mineOnly) => set({ mineOnly }),

  fetchOpenPicklists: async () => {
    set({ isLoadingOpen: true, openError: null });
    try {
      const openPicklists = await picklistService.getOpen(!get().mineOnly);
      set({ openPicklists, isLoadingOpen: false });
      // 86d3mnuxe — overlay the outbox posting jobs onto each row's activePostingBatches (one bulk call),
      // non-blocking so the list renders immediately (perf gate). Drives the per-row posting indicator +
      // the cross-session auto-refresh loop (PicklistsTab), since getOpen returns activePostingBatches [].
      void (async () => {
        let jobs: OutboxPostingStatusDto[];
        try {
          jobs = await fetchActiveOutboxStatuses(outboxType.picklist);
        } catch {
          return;
        }
        // Nothing posting → getOpen already returned activePostingBatches []; skip the re-map (avoids
        // reference churn + an extra render in the common case).
        if (jobs.length === 0) return;
        const byId = groupOutboxByDocId(jobs);
        set((state) => ({
          openPicklists: state.openPicklists.map((p) => ({
            ...p,
            activePostingBatches: (byId.get(p.id) ?? []).map(mapOutboxJobToBatch),
          })),
        }));
      })();
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch open picklists');
      set({ openError: message, isLoadingOpen: false });
    }
  },

  // A failed count leaves the pills as skeletons rather than showing a wrong number or an error banner —
  // the counts decorate the tabs, they are not the page.
  fetchCounts: async () => {
    try {
      const counts = await picklistService.getCounts(!get().mineOnly);
      set({ counts });
    } catch {
      set({ counts: null });
    }
  },

  fetchClosedPicklists: async (params: ClosedPagedParams) => {
    set({ isLoadingClosed: true, closedError: null });
    try {
      const result = await picklistService.getClosedPaged({
        ...params,
        viewAll: !get().mineOnly,
      });
      set({
        closedPicklists: result.items,
        closedTotalCount: result.totalCount,
        closedTotalPages: result.totalPages,
        isLoadingClosed: false,
      });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch closed picklists');
      set({ closedError: message, isLoadingClosed: false });
    }
  },

  fetchPicklistById: async (id: number) => {
    // Already on screen: a refresh after an action, through the one path. Quiet — a failure keeps what is shown and
    // the next read retries; the page-wide error belongs to a picklist that never loaded.
    if (get().selectedPicklist?.id === id) {
      await picklistRefresh.refresh(id);
      return;
    }
    if (firstLoad?.id === id) return firstLoad.done;
    const token = {};
    const load = (async () => {
      set({ isLoadingDetail: true, detailError: null });
      try {
        // ALWAYS opts in to view-all, exactly as the /activity endpoint does and for the same reason: a
        // TABLE FILTER must never decide whether a record can be opened. It is not an escalation — the
        // server's CanViewAllPicklists() grants it only to `issuance.view_all_picklists`, so a user without
        // that permission still gets the creator-or-picker rule and a 404 for anyone else's picklist.
        // Passing the toggle here is what made "show only mine" 404 every picklist the user had not created.
        const picklist = await picklistService.getById(id, true);
        if (firstLoad?.token !== token) return; // superseded by another picklist's first load
        set({ selectedPicklist: picklist, isLoadingDetail: false });
        // 86d3mnuxe — render the picklist now; stream the outbox-projected posting cards in without blocking
        // the panel (perf gate). The ordered read (outbox, then picklist) starts any settle-poll an in-flight post needs.
        void picklistRefresh.refresh(id);
      } catch (error) {
        if (firstLoad?.token !== token) return;
        const message = getApiErrorMessage(error, 'Failed to fetch picklist');
        set({ detailError: message, isLoadingDetail: false });
      }
    })().finally(() => {
      if (firstLoad?.token === token) firstLoad = null;
    });
    firstLoad = { id, token, done: load };
    return load;
  },

  clearSelectedPicklist: () => {
    picklistRefresh.reset();
    set({ selectedPicklist: null, detailError: null });
  },

  postPicklist: async (items: PostPicklistItemRequest[], reasonForAdjustmentId: number, requestedByEmployeeId: string, requestedByName: string | undefined, itemMemos?: Record<number, string>, itemReadings?: Record<number, number | null>, clientKey?: string) => {
    const picklist = usePicklistStore.getState().selectedPicklist;
    if (!picklist) return { error: 'No picklist selected' };
    const picklistId = picklist.id;

    set({ isPosting: true });
    let batchId: number;
    try {
      batchId = await picklistService.claimPost({
        picklistId,
        items,
        reasonForAdjustmentId,
        requestedByEmployeeId,
        requestedByName,
        itemMemos,
        itemReadings,
        clientKey,
      });
    } catch (error) {
      set({ isPosting: false });
      return { error: getApiErrorMessage(error, 'Failed to start MIS posting') };
    }

    // 202 accepted (86d3kwt7h) — optimistically show a Posting card and start the settle-poll. The dialog
    // closes immediately (the caller toasts "Posting started"); the worker does the NetSuite post.
    set({ isPosting: false });
    usePicklistStore.getState().pollPostingBatch(picklistId, batchId, true);
    // No Activity bump here (owner 2026-10-08): pressing Post writes no activity entry — "posted to NetSuite as MIS-…"
    // is written when NetSuite confirms — so a bump only showed the panel's arrival skeleton for ~10s and then a false
    // "Still recording". The settle-poll bumps when the batch settles, which is when the entry exists.
    return batchId;
  },

  retryPosting: async (postingBatchId: number) => {
    const picklistId = usePicklistStore.getState().selectedPicklist?.id;
    if (!picklistId) return { error: 'No picklist selected' };
    try {
      await picklistService.retryMisPost(postingBatchId);
    } catch (error) {
      return { error: getApiErrorMessage(error, 'Failed to retry MIS posting') };
    }
    usePicklistStore.getState().pollPostingBatch(picklistId, postingBatchId, true);
    // No Activity bump: same reason as postPicklist — the entry is written when the batch settles.
    return postingBatchId;
  },

  hydratePostingCards: async (picklistId: number) => {
    // 86d3mnuxe — the posting cards are the outbox's, read before the picklist (the order readPicklist keeps), and any
    // in-flight post gets its settle-poll from applyPicklist. One read, through the one path.
    await picklistRefresh.refresh(picklistId);
  },

  // Fire-and-forget: optimistically mark the batch Posting, then poll until it settles. Source of truth for
  // the active/failed cards is the OUTBOX (86d3mnuxe) — getById returns the picklist data + settled MIS refs,
  // /api/outbox/status returns the in-flight/failed jobs. Self-terminating on the BATCH's own state (PW-22 —
  // it runs while the batch is open, with no wall-clock cap) + id-guarded, so navigating away is safe; the
  // notification service will replace this poll later.
  pollPostingBatch: (picklistId: number, postingBatchId: number, addOptimistic: boolean) => {
    if (addOptimistic) {
      set((state) => {
        if (state.selectedPicklist?.id !== picklistId) return {};
        const optimistic: PostingBatchStatusDto = {
          id: postingBatchId,
          status: PostingStatusEnum.Posting,
          misTranId: null,
          postingFailedReason: null,
          postingClaimedAt: new Date().toISOString(),
          postReach: 0, // Unknown — a local optimistic stand-in; the outbox refetch replaces it
          netSuiteRecordId: null,
        };
        const exists = state.selectedPicklist.activePostingBatches.some((b) => b.id === postingBatchId);
        return {
          selectedPicklist: {
            ...state.selectedPicklist,
            activePostingBatches: exists
              ? state.selectedPicklist.activePostingBatches.map((b) => (b.id === postingBatchId ? optimistic : b))
              : [...state.selectedPicklist.activePostingBatches, optimistic],
          },
        };
      });
    }

    if (activePolls.has(postingBatchId)) return; // a settle-poll for this batch is already running
    activePolls.add(postingBatchId);
    set({ postingTransport: { state: 'updating', lastCheckedAt: get().postingTransport.lastCheckedAt } });
    void (async () => {
      try {
        const start = Date.now();
        // PW-22 — the loop's exit condition is the BATCH, not the clock: it runs while this batch is open
        // and returns the moment it is not. The old five-minute cap made the screen stop asking while the
        // server was still working, and said nothing when it did.
        for (;;) {
          await new Promise((r) => setTimeout(r, pollDelayMs(Date.now() - start)));
          // Nothing is fetched while the tab is hidden; the tick that came due fires the moment it returns.
          await whenVisible();
          if (usePicklistStore.getState().selectedPicklist?.id !== picklistId) return; // navigated away
          // Through the one path: a read no older than this tick's own cadence is reused, so two open batches —
          // two loops — cost one read, not two, and a read already running is joined rather than doubled.
          const error = await picklistRefresh.refreshIfStale(
            picklistId, Math.max(pollDelayMs(Date.now() - start) - 500, 1_000),
          ).done;
          if (error) {
            // PW-22 — a failed tick is VISIBLE; it used to be silent, so stalled and healthy looked identical.
            set({ postingTransport: { ...get().postingTransport, state: 'stalled' } });
            continue; // transient — keep polling
          }
          const current = usePicklistStore.getState().selectedPicklist;
          if (current?.id !== picklistId) return; // navigated away
          const activeBatches = current.activePostingBatches;
          // Done → the job drops off the projection; Failed → it stays as PostingFailed. Either way this
          // batch is no longer "Posting", so stop (a Failed card persists for Retry).
          const stillPosting = activeBatches.some(
            (b) => b.id === postingBatchId && b.status === PostingStatusEnum.Posting,
          );
          if (!stillPosting) {
            // The ONLY bump in this loop, and it stays that way: it refreshes the activity dock when the
            // MisPosted row lands. Bumping per tick (or on focus) fans out requests and produces a false
            // "Still recording" banner. QUIET (owner 2026-10-08: no placeholder at all when posting): it still
            // polls for the row, but the panel draws no skeleton and no "Still recording" while it does.
            useActivityRefreshStore.getState().bumpQuietly();
            return;
          }
        }
      } finally {
        activePolls.delete(postingBatchId);
        // "stopped" means no batch on this picklist is open any more — not that a clock ran out.
        if (activePolls.size === 0) {
          set({ postingTransport: { ...get().postingTransport, state: 'stopped' } });
        }
      }
    })();
  },

  refreshPostingCards: async (picklistId: number) => {
    const now = Date.now();
    if (now - lastManualRefreshAt < REFRESH_DEBOUNCE_MS) return;
    lastManualRefreshAt = now;
    // The one path: its read keeps the outbox-then-picklist order and stamps lastCheckedAt when it lands.
    await picklistRefresh.refresh(picklistId);
  },

  addItems: async (items: CreatePicklistItemRequest[]) => {
    const picklist = usePicklistStore.getState().selectedPicklist;
    if (!picklist) return false;

    set({ isEditing: true });
    try {
      const updated = await picklistService.addItems(picklist.id, { items });
      set({ selectedPicklist: updated, isEditing: false });
      useActivityRefreshStore.getState().bump();
      return true;
    } catch (error) {
      console.error('Failed to add items:', error);
      set({ isEditing: false });
      throw error;
    }
  },

  // The quiet reload is the one path's `refresh`: no skeleton, no page error, the posting cards read with it.
  reloadSelectedPicklist: (id: number) => picklistRefresh.refresh(id),

  refreshPicklistIfStale: (id: number, maxAgeMs: number) => picklistRefresh.refreshIfStale(id, maxAgeMs).started,

  removeItems: async (picklistItemIds: number[]) => {
    const picklist = usePicklistStore.getState().selectedPicklist;
    if (!picklist) return false;

    set({ isEditing: true });
    try {
      const updated = await picklistService.removeItems(picklist.id, { picklistItemIds });
      set({ selectedPicklist: updated, isEditing: false });
      useActivityRefreshStore.getState().bump();
      // PT-7 A13 — the answer is the RAW read, with none of the by-id folds: posting figures, `takenBy` and the
      // MRS summaries read empty until a by-id read. Refetch quietly, not awaited, so the success toast shows
      // at once; a failed refetch stays quiet (the mutation succeeded, and the next read corrects the figures).
      void usePicklistStore.getState().reloadSelectedPicklist(picklist.id);
      return true;
    } catch (error) {
      console.error('Failed to remove items:', error);
      set({ isEditing: false });
      throw error;
    }
  },

  adjustItem: async (picklistItemId: number, request: AdjustPicklistItemRequest) => {
    const picklist = usePicklistStore.getState().selectedPicklist;
    if (!picklist) return false;

    set({ isAdjusting: true });
    try {
      const updated = await picklistService.adjustItem(picklist.id, picklistItemId, request);
      // PT-6 A — the by-id shape; its activePostingBatches is [] (the outbox is their source), so keep ours.
      const current = usePicklistStore.getState().selectedPicklist;
      set({
        selectedPicklist: current?.id === updated.id
          ? { ...updated, activePostingBatches: current.activePostingBatches }
          : updated,
        isAdjusting: false,
      });
      // Tell any mounted ActivityList to refetch — the audit row is already
      // committed (same DB transaction as the adjustment), so the rail will
      // surface the new event without a page refresh.
      useActivityRefreshStore.getState().bump();
      return true;
    } catch (error) {
      // The dialog classifies and shows it (B4); a handled refusal is not a console error.
      set({ isAdjusting: false });
      throw error;
    }
  },

  replaceSerial: async (picklistItemId: number, request: ReplaceSerialRequest) => {
    const picklist = usePicklistStore.getState().selectedPicklist;
    if (!picklist) return false;

    set({ isReplacing: true });
    try {
      // The answer is the by-id shape (PT-6 A), so the card's taken state clears from it directly.
      const updated = await picklistService.replaceSerial(picklist.id, picklistItemId, request);
      const current = usePicklistStore.getState().selectedPicklist;
      set({
        selectedPicklist: current?.id === updated.id
          ? { ...updated, activePostingBatches: current.activePostingBatches }
          : updated,
        isReplacing: false,
      });
      useActivityRefreshStore.getState().bump();
      return true;
    } catch (error) {
      set({ isReplacing: false });
      throw error;
    }
  },

  closePicklist: async (reason?: string) => {
    const picklist = usePicklistStore.getState().selectedPicklist;
    if (!picklist) return 'Failed to close picklist';

    set({ isClosing: true });
    try {
      const updated = await picklistService.close(picklist.id, reason);
      set({ selectedPicklist: updated, isClosing: false });
      useActivityRefreshStore.getState().bump();
      return true;
    } catch (error) {
      console.error('Failed to close picklist:', error);
      set({ isClosing: false });
      return getApiErrorMessage(error, 'Failed to close picklist');
    }
  },

  cancelPicklist: async (reason: string) => {
    const picklist = usePicklistStore.getState().selectedPicklist;
    if (!picklist) return 'Failed to cancel picklist';

    set({ isCancelling: true });
    try {
      const updated = await picklistService.cancel(picklist.id, reason);
      set({ selectedPicklist: updated, isCancelling: false });
      useActivityRefreshStore.getState().bump();
      return true;
    } catch (error) {
      console.error('Failed to cancel picklist:', error);
      set({ isCancelling: false });
      return getApiErrorMessage(error, 'Failed to cancel picklist');
    }
  },

  addPicker: async (userId: number) => {
    const picklist = usePicklistStore.getState().selectedPicklist;
    if (!picklist) return false;

    set({ isManagingPickers: true });
    try {
      const updated = await picklistService.addPicker(picklist.id, userId);
      set({ selectedPicklist: updated, isManagingPickers: false });
      useActivityRefreshStore.getState().bump();
      // PT-7 A13 — the raw read again; see removeItems.
      void usePicklistStore.getState().reloadSelectedPicklist(picklist.id);
      return true;
    } catch (error) {
      console.error('Failed to add picker:', error);
      set({ isManagingPickers: false });
      throw error;
    }
  },

  removePicker: async (userId: number) => {
    const picklist = usePicklistStore.getState().selectedPicklist;
    if (!picklist) return false;

    set({ isManagingPickers: true });
    try {
      const updated = await picklistService.removePicker(picklist.id, userId);
      set({ selectedPicklist: updated, isManagingPickers: false });
      // PW-08 — removing a picker now writes a PickerRemoved activity row, so the dock has to be told, the
      // same way addPicker above does. Without this the new row sits invisible until some unrelated
      // mutation happens to bump, which reads as "the removal was not recorded".
      useActivityRefreshStore.getState().bump();
      // PT-7 A13 — the raw read again; see removeItems.
      void usePicklistStore.getState().reloadSelectedPicklist(picklist.id);
      return true;
    } catch (error) {
      console.error('Failed to remove picker:', error);
      set({ isManagingPickers: false });
      throw error;
    }
  },
}), {
  name: 'csc-wms-picklist-prefs',
  storage: createJSONStorage(() => localStorage),
  // ONLY the scope preference. Everything else in this store is server data with a freshness contract —
  // persisting a picklist list would show yesterday's quantities on first paint, before any fetch lands.
  partialize: (state) => ({ mineOnly: state.mineOnly }),
}));

/**
 * PW-22 — everything a batch surface needs to know about how its data is arriving, and nothing about how it
 * gets here.
 *
 * The strip should be able to say something true — "Updating · last checked 14:03", "Not updating" — without
 * importing a poll interval, a ceiling, or an `EventSource`. If the transport is ever replaced (the store
 * already anticipates a notification service taking this over), only this hook changes.
 *
 * ⚠️ Copy built on it must describe polling, not streaming: there is no MIS stream, nothing heartbeats, and
 * nothing is replayed on reconnect. A "Live" pill here would be a claim the transport cannot honour.
 */
export function usePostingTransport() {
  const state = usePicklistStore((s) => s.postingTransport.state);
  const lastCheckedAt = usePicklistStore((s) => s.postingTransport.lastCheckedAt);
  const refresh = usePicklistStore((s) => s.refreshPostingCards);
  return { state, lastCheckedAt, refresh };
}
